/**
 * React Hook Form + shadcn Select 필드.
 * FormTextField와 동일한 라벨·필수 마크·에러 패턴을 유지한다.
 *
 * - 옵션 목록에 공란(empty) 항목을 두지 않는다.
 * - 값이 null/빈 문자열이면 트리거에 placeholder(기본: 라벨명)를 표시한다.
 */
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import * as React from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

export interface FormSelectOption {
  value: string;
  label: string;
}

export interface FormSelectFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label: React.ReactNode;
  options: FormSelectOption[];
  required?: boolean;
  /** 미지정 시 라벨이 문자열이면 라벨명, 아니면 '선택' */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const FormSelectField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  options,
  required,
  placeholder,
  disabled,
  className,
}: FormSelectFieldProps<TFieldValues, TName>) => {
  const resolvedPlaceholder =
    placeholder ?? (typeof label === 'string' ? label : '선택');

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const hasValue =
          field.value != null && String(field.value).trim() !== '';
        const selected = hasValue ? String(field.value) : undefined;

        return (
          <FormItem className={className}>
            <FormLabel required={required}>{label}</FormLabel>
            <Select
              // 빈 값(uncontrolled) → 선택 후 controlled 전환 시 경고 방지
              key={hasValue ? 'selected' : 'empty'}
              disabled={disabled}
              value={selected}
              onValueChange={field.onChange}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={resolvedPlaceholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
